import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../shared/material/material.module';
import { modalFadeIn } from '../../shared/animations';

/**
 * AddPayerDialogComponent
 * Dialog para adicionar CNPJ de pagador externo a um plano
 * Requirement: 2.2 - Permitir adicionar pagador após criação do plano
 */
@Component({
  selector: 'app-add-payer-dialog',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule],
  templateUrl: './add-payer-dialog.component.html',
  styleUrl: './add-payer-dialog.component.scss',
  animations: [modalFadeIn]
})
export class AddPayerDialogComponent {
  dialogRef = inject(MatDialogRef<AddPayerDialogComponent>);

  payerCnpj = signal('');
  cnpjError = signal('');

  /**
   * Valida formato do CNPJ
   * Aceita formatos: 12.345.678/0001-90 ou 12345678000190
   */
  validateCnpj(): boolean {
    const cnpj = this.payerCnpj().trim();
    
    if (!cnpj) {
      this.cnpjError.set('CNPJ é obrigatório');
      return false;
    }

    // Remove caracteres especiais
    const cleanCnpj = cnpj.replace(/[^\d]/g, '');

    // Valida tamanho
    if (cleanCnpj.length !== 14) {
      this.cnpjError.set('CNPJ deve ter 14 dígitos');
      return false;
    }

    // Valida se não são todos dígitos iguais
    if (/^(\d)\1+$/.test(cleanCnpj)) {
      this.cnpjError.set('CNPJ inválido');
      return false;
    }

    this.cnpjError.set('');
    return true;
  }

  /**
   * Formata CNPJ enquanto digita
   */
  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^\d]/g, '');

    // Limita a 14 dígitos
    if (value.length > 14) {
      value = value.substring(0, 14);
    }

    // Aplica máscara: 12.345.678/0001-90
    if (value.length > 12) {
      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    } else if (value.length > 8) {
      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
    } else if (value.length > 5) {
      value = value.replace(/^(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
    } else if (value.length > 2) {
      value = value.replace(/^(\d{2})(\d{0,3})/, '$1.$2');
    }

    this.payerCnpj.set(value);
    
    // Limpa erro ao digitar
    if (this.cnpjError()) {
      this.cnpjError.set('');
    }
  }

  /**
   * Confirma e retorna o CNPJ (apenas números)
   */
  onConfirm(): void {
    if (this.validateCnpj()) {
      // Retorna apenas os números do CNPJ
      const cleanCnpj = this.payerCnpj().replace(/[^\d]/g, '');
      this.dialogRef.close(cleanCnpj);
    }
  }

  /**
   * Cancela e fecha o dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }
}
